import type { Ref } from 'vue';
import { computed } from 'vue';
import { unref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getApi } from './api';

export type PlaceSuggestion = {
  id: string;
  score: { composite_pct: number };
  stats: {
    device_count: number;
    visit_count: number;
    distinct_days: number;
    avg_visit_seconds: number;
    total_dwell_seconds: number;
    first_seen_t: number;
    last_seen_t: number;
  };
  generated_at: string;
  name?: string | null;
  address?: string | null;
  rationale?: string | null;
  polygon?: PlaceSuggestionPolygon | null;
};

export type PlaceSuggestionPolygon = {
  type: 'Polygon';
  coordinates: number[][][];
};

export default function getAccountPlaceSuggestions(
  accountId: Ref<number | null | undefined> | number | null | undefined,
) {
  const query = useQuery({
    queryKey: ['place-suggestions', () => unref(accountId)],
    queryFn: async () => {
      const aid = unref(accountId);
      if (aid == null || aid === 0) {
        return { suggestions: [] as PlaceSuggestion[], total: 0 };
      }
      const { api, baseURL } = getApi();
      const res = await api.get(`api/v2/accounts/${aid}/place-suggestions`, {
        baseURL,
        useOriginalToken: true,
      });
      return {
        suggestions: (res.data?.data ?? []) as PlaceSuggestion[],
        total: Number(res.data?.meta?.total ?? 0),
      };
    },
    enabled: () => {
      const aid = unref(accountId);
      return aid != null && aid !== 0;
    },
    staleTime: 5 * 60 * 1000,
  });

  const suggestions = computed(() => query.data.value?.suggestions ?? []);
  const count = computed(() => query.data.value?.total ?? 0);

  return {
    ...query,
    suggestions,
    count,
  };
}
